import { useBearingsTableStore } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { lifetimeConfigParams, spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import type {
  LifetimeConfigParams,
  LifetimeConfigTypeOfOperation,
  SpatialTypeOfMovement,
  TemporalTypeOfMovement,
} from '@hub/bearing-hub/common/types';
import { lifetimeConfigTypeOfOperation, temporalTypeOfMovement } from '@hub/bearing-hub/common/types';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
  getDefaultTemperatureServiceLife,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { FilterSelectAutocomplete } from '@hub/common/inputs/FilterSelectAutocomplete';
import type { ButtonGroupDefinition } from '@hub/common/ButtonGroup/ButtonGroup';
import { ButtonGroup } from '@hub/common/ButtonGroup/ButtonGroup';
import { Button, TYPE } from '@hub/common/Button/Button';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import type { UnitCode } from '@icommerce/units';
import {
  AngleCode,
  DurationCode,
  ForceCode,
  getUnitLabel,
  getUnitLabelForUnitSystem,
  LinearFrequencyCode,
  RotationalFrequencyCode,
  TemperatureCode,
} from '@icommerce/units';
import { BearingHubHelper, SortingState } from '@hub/libs/bearing-hub.helper';
import { getLocale } from '@hub/libs/constants';
import { scrollToElement } from '@hub/libs/utils';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { FilterResetLink } from '@hub/common/FilterResetLink/FilterResetLink';
import type { OppositeMaterialKeyType } from './LifeTimePopUpInfo';
import { OppositeMaterialMap } from './LifeTimePopUpInfo';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { defaultLifetimeFormConfig as trsConfig } from '@igus/tribology-domain';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import type { LifetimeInputFieldConfig } from '@igus/tribology-domain/src/lib/lifetime/lifetime-calculator-definitions';
import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import { BasicNumberInput } from '@hub/common/BasicNumberInput/BasicNumberInput';

type LifetimeParameterInput = {
  title: string;
  attribute: LifetimeConfigParams;
  attributeText: string;
  unit?: string;
  readOnly?: boolean;
  defaultValue: number | string;
  inputType: LifetimeParameterInputType;
  buttonGroupDefinition?: ButtonGroupDefinition<TemporalTypeOfMovement>[];
  hint?: string;
};

enum LifetimeParameterInputType {
  numberInput = 'numberInput',
  filterSelectAutocomplete = 'filterSelectAutocomplete',
  buttonGroup = 'buttonGroup',
  none = 'none',
}

enum LifetimeParameterInputSectionType {
  environment = 'environment',
  materials = 'materials',
  operation = 'operation',
  motion = 'motion',
}

type LifetimeParameterInputSection = {
  type: LifetimeParameterInputSectionType;
  title: string;
  items: {
    [key in LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default']?: LifetimeParameterInput[];
  };
};

interface LifetimeParameterFormProps {
  unitSystem: IbhUnitSystem;
  isEmbedded?: boolean;
  onCalculate: () => void;
  onReset: () => void;
  shapeKey: IbhShape;
}

export const LifetimeParametersForm = ({
  unitSystem,
  onCalculate,
  onReset,
  isEmbedded,
  shapeKey,
}: LifetimeParameterFormProps) => {
  const { t } = useTranslation(['common']);
  const { lifetimeParameters, setLifetimeParametersValue, hasLifetimeParameterValues } = useLifetimeParametersStore();
  const { setTableHeadersSortingState } = useBearingsTableStore();
  const dimensions = useDimensionsStore(state => state.dimensions);
  const { CustomEvent } = usePiwikPro();
  const router = useRouter();
  const locale = getLocale(router);
  const innerDiameter = useMemo(() => dimensions[shapeKey]?.d1, [dimensions, shapeKey]);
  const bearingWidth: number | undefined = useMemo(() => {
    const dimension = dimensions[shapeKey];
    if (dimension && 'b1' in dimension) {
      return dimension.b1;
    }
    return undefined;
  }, [dimensions, shapeKey]);
  const showSurfacePressure = useMemo(() => !!innerDiameter && !!bearingWidth, [innerDiameter, bearingWidth]);
  const [isFormValid, setFormValidation] = useState(true);
  const [hintMessage, setHintMessage] = useState<string>(undefined);
  const ROUNDING_PRECISION = 10_000;
  const temperatureText = unitSystem === IbhUnitSystem.Imperial ? '73.4 °F' : '23 °C';
  // map trs config properties to the ones used by IBD
  const defaultLifetimeFormConfig = {
    ...trsConfig,
    offTime: trsConfig.intermittentOffTime,
    onTime: trsConfig.intermittentOnTime,
    oppositeMaterialId: trsConfig.oppositeMaterial,
  };

  const onLifetimeParameterValueChange = (key: LifetimeConfigParams, value: number | string | undefined) => {
    setLifetimeParametersValue(key, value);

    if (!showSurfacePressure || value === undefined) {
      return;
    }

    const surface = innerDiameter * bearingWidth;

    if (key === lifetimeConfigParams.surfacePressure) {
      setLifetimeParametersValue(
        lifetimeConfigParams.force,
        Math.round(Number(value) * surface * ROUNDING_PRECISION) / ROUNDING_PRECISION
      );
    }

    if (key === lifetimeConfigParams.force) {
      setLifetimeParametersValue(
        lifetimeConfigParams.surfacePressure,
        Math.round((Number(value) / surface) * ROUNDING_PRECISION) / ROUNDING_PRECISION
      );
    }
  };

  const inputsDefinitions: LifetimeParameterInputSection[] = [
    {
      type: LifetimeParameterInputSectionType.environment,
      title: 'LIFETIME.SECTION_ENVIRONMENT',
      items: {
        default: [
          {
            title: 'LIFETIME.ENVIRONMENT',
            attribute: lifetimeConfigParams.environment,
            attributeText: 'LIFETIME.ENVIRONMENT',
            defaultValue: DefaultServiceLifeForBearings.environment,
            readOnly: true,
            inputType: LifetimeParameterInputType.none,
            hint: t(`LIFETIME.ENVIRONMENT_HINT`, {
              ns: 'bearing-hub/bearingHub',
              temperature: temperatureText,
            }),
          },
          {
            title: 'LIFETIME.TEMPERATURE',
            attribute: lifetimeConfigParams.temperature,
            attributeText: 'LIFETIME.TEMPERATURE',
            defaultValue: getDefaultTemperatureServiceLife(unitSystem),
            readOnly: true,
            unit: getUnitLabelForUnitSystem(TemperatureCode.CELSIUS, BearingHubHelper.toUnitSystem(unitSystem)),
            inputType: LifetimeParameterInputType.none,
            hint: t(`LIFETIME.TEMPERATURE_HINT`, { ns: 'bearing-hub/bearingHub', temperature: temperatureText }),
          },
          {
            title: 'LIFETIME.LOAD',
            attribute: lifetimeConfigParams.force,
            attributeText: 'LIFETIME.LOAD',
            defaultValue:
              unitSystem === IbhUnitSystem.Metric
                ? (DefaultServiceLifeForBearings.load as { force: number }).force
                : DefaultServiceLifeForBearingsForUS.load.force,
            unit: getUnitLabelForUnitSystem(ForceCode.NEWTON, BearingHubHelper.toUnitSystem(unitSystem)),
            inputType: LifetimeParameterInputType.numberInput,
          },
          {
            title: 'LIFETIME.SURFACE_PRESSURE',
            attribute: lifetimeConfigParams.surfacePressure,
            attributeText: 'LIFETIME.SURFACE_PRESSURE',
            defaultValue:
              (DefaultServiceLifeForBearings.load as { force: number }).force / (innerDiameter * bearingWidth),
            unit: `${getUnitLabelForUnitSystem(
              ForceCode.NEWTON,
              BearingHubHelper.toUnitSystem(unitSystem)
            )}/${BearingHubHelper.getMetricType(unitSystem)}²`,
            inputType: LifetimeParameterInputType.numberInput,
          },
          {
            title: 'LIFETIME.ACCEPTABLE_WEAR',
            attribute: lifetimeConfigParams.acceptableWear,
            attributeText: 'LIFETIME.ACCEPTABLE_WEAR',
            defaultValue: DefaultServiceLifeForBearings.acceptableWear,
            unit: BearingHubHelper.getMetricType(unitSystem),
            inputType: LifetimeParameterInputType.numberInput,
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.materials,
      title: 'LIFETIME.SECTION_MATERIALS',
      items: {
        default: [
          {
            title: '',
            attribute: lifetimeConfigParams.oppositeMaterialId,
            attributeText: '',
            defaultValue: DefaultServiceLifeForBearings.oppositeMaterialId,
            inputType: LifetimeParameterInputType.filterSelectAutocomplete,
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.operation,
      title: 'LIFETIME.SECTION_TYPE_OF_OPERATION',
      items: {
        [lifetimeConfigTypeOfOperation.continous]: [
          {
            title: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            attribute: lifetimeConfigParams.activityMinutesPerHour,
            attributeText: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            defaultValue: DefaultServiceLifeForBearings.activityMinutesPerHour,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.MINUTE),
          },
        ],
        [lifetimeConfigTypeOfOperation.intermittent]: [
          {
            title: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            attribute: lifetimeConfigParams.activityMinutesPerHour,
            attributeText: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            defaultValue: DefaultServiceLifeForBearings.activityMinutesPerHour,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.MINUTE),
          },
          {
            title: 'LIFETIME.ON_TIME',
            attribute: lifetimeConfigParams.onTime,
            attributeText: 'LIFETIME.ON_TIME',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.SECOND),
          },
          {
            title: 'LIFETIME.OFF_TIME',
            attribute: lifetimeConfigParams.offTime,
            attributeText: 'LIFETIME.OFF_TIME',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.SECOND),
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.motion,
      title: 'LIFETIME.SECTION_TYPE_OF_MOTION',
      items: {
        [spatialTypeOfMovement.rotation]: [
          {
            title: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            attribute: lifetimeConfigParams.rotationRevolutions,
            attributeText: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            defaultValue: DefaultServiceLifeForBearings.typeOfMovement,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(
              (locale === 'en'
                ? RotationalFrequencyCode.ROTATIONAL_REVOLUTION_PER_MINUTE
                : RotationalFrequencyCode.REVOLUTION_PER_MINUTE) as UnitCode
            ),
          },
        ],
        [spatialTypeOfMovement.linear]: [
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: unitSystem,
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.REVOLUTION_PER_MINUTE,
              BearingHubHelper.toUnitSystem(unitSystem)
            ),
          },
        ],
        [spatialTypeOfMovement.pivoting]: [
          {
            title: 'LIFETIME.PIVOTING_ANGLE',
            attribute: lifetimeConfigParams.pivotingAngle,
            attributeText: 'LIFETIME.PIVOTING_ANGLE',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(AngleCode.DEGREE),
          },
          {
            title: 'LIFETIME.PIVOTING_FREQUENCY',
            attribute: lifetimeConfigParams.pivotingFrequency,
            attributeText: 'LIFETIME.PIVOTING_FREQUENCY',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
        ],
        [spatialTypeOfMovement.linearAndRotation]: [
          {
            title: 'LIFETIME.CONNECTION_OF_THE_MOVEMENTS',
            attribute: lifetimeConfigParams.temporalTypeOfMovement,
            attributeText: 'LIFETIME.TYPE_OF_OPERATION',
            defaultValue: DefaultServiceLifeForBearings.temporalTypeOfMovement,
            inputType: LifetimeParameterInputType.buttonGroup,
            buttonGroupDefinition: [
              {
                text: t('LIFETIME.ONE_AFTER_ANOTHER', { ns: 'bearing-hub/bearingHub' }),
                value: temporalTypeOfMovement.sequential,
              },
              {
                text: t('LIFETIME.AT_THE_SAME_TIME', { ns: 'bearing-hub/bearingHub' }),
                value: temporalTypeOfMovement.simultaneous,
              },
            ],
          },
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: unitSystem,
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.REVOLUTION_PER_MINUTE,
              BearingHubHelper.toUnitSystem(unitSystem)
            ),
          },
          {
            title: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            attribute: lifetimeConfigParams.rotationRevolutions,
            attributeText: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            defaultValue: DefaultServiceLifeForBearings.typeOfMovement,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
          {
            title: 'LIFETIME.TEMPORAL_LINEAR_PART',
            attribute: lifetimeConfigParams.temporalLinearPart,
            attributeText: 'LIFETIME.TEMPORAL_LINEAR_PART',
            defaultValue: DefaultServiceLifeForBearings.temporalLinearPart,
            inputType: LifetimeParameterInputType.numberInput,
            unit: null,
          },
        ],
        [spatialTypeOfMovement.linearAndPivoting]: [
          {
            title: 'LIFETIME.CONNECTION_OF_THE_MOVEMENTS',
            attribute: lifetimeConfigParams.temporalTypeOfMovement,
            attributeText: 'LIFETIME.TYPE_OF_OPERATION',
            defaultValue: DefaultServiceLifeForBearings.temporalTypeOfMovement,
            inputType: LifetimeParameterInputType.buttonGroup,
            buttonGroupDefinition: [
              {
                text: t('LIFETIME.ONE_AFTER_ANOTHER', { ns: 'bearing-hub/bearingHub' }),
                value: temporalTypeOfMovement.sequential,
              },
              {
                text: t('LIFETIME.AT_THE_SAME_TIME', { ns: 'bearing-hub/bearingHub' }),
                value: temporalTypeOfMovement.simultaneous,
              },
            ],
          },
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: unitSystem,
          },
          {
            title: 'LIFETIME.PIVOTING_ANGLE',
            attribute: lifetimeConfigParams.pivotingAngle,
            attributeText: 'LIFETIME.PIVOTING_ANGLE',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: `[${getUnitLabel(AngleCode.DEGREE)}]`,
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.REVOLUTION_PER_MINUTE,
              BearingHubHelper.toUnitSystem(unitSystem)
            ),
          },
          {
            title: 'LIFETIME.PIVOTING_FREQUENCY',
            attribute: lifetimeConfigParams.pivotingFrequency,
            attributeText: 'LIFETIME.PIVOTING_FREQUENCY',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
          {
            title: 'LIFETIME.TEMPORAL_LINEAR_PART',
            attribute: lifetimeConfigParams.temporalLinearPart,
            attributeText: 'LIFETIME.TEMPORAL_LINEAR_PART',
            defaultValue: DefaultServiceLifeForBearings.temporalLinearPart,
            inputType: LifetimeParameterInputType.numberInput,
            unit: null,
          },
        ],
      },
    },
  ];

  const getLifetimeParameterType = (
    type: LifetimeParameterInputSectionType
  ): LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default' => {
    let currentItemType: LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default' = 'default';
    if (type === LifetimeParameterInputSectionType.operation) {
      currentItemType = lifetimeParameters.typeOfOperation;
    } else if (type === LifetimeParameterInputSectionType.motion) {
      currentItemType = lifetimeParameters.typeOfMovement;
    }
    return currentItemType;
  };

  const getProperValuesForUnitSystem = (
    value: number,
    attributeConfig: LifetimeInputFieldConfig,
    unitSystemValue: IbhUnitSystem
  ): number =>
    unitSystemValue === IbhUnitSystem.Imperial && attributeConfig.toImperial
      ? Number(attributeConfig.toImperial(value).toFixed(2))
      : value;

  const inputValidation = (
    value: string | number,
    attributeConfig: LifetimeInputFieldConfig
  ): { min: number; max: number; isValid: boolean } => {
    const min = getProperValuesForUnitSystem(attributeConfig?.min, attributeConfig, unitSystem);
    const max = getProperValuesForUnitSystem(attributeConfig?.max, attributeConfig, unitSystem);

    if (!value && value !== 0) {
      return { isValid: false, min, max };
    }

    return { isValid: Number(value) >= min && Number(value) <= max, min, max };
  };

  useEffect(() => {
    const inputs = inputsDefinitions
      .map(({ items, type }) =>
        items[getLifetimeParameterType(type)].filter(item => {
          return item.attribute === lifetimeConfigParams.surfacePressure
            ? showSurfacePressure
            : !item.readOnly && item.inputType === LifetimeParameterInputType.numberInput;
        })
      )
      .flat();

    const valid = inputs.every(item => {
      const attributeConfig = defaultLifetimeFormConfig?.[item.attribute] as LifetimeInputFieldConfig;
      const value = lifetimeParameters?.[item.attribute] as string;

      return inputValidation(value, attributeConfig).isValid;
    });
    setFormValidation(valid);
  }, [lifetimeParameters, defaultLifetimeFormConfig, inputsDefinitions, showSurfacePressure]);

  useEffect(() => {
    const force = lifetimeParameters?.force;
    if (showSurfacePressure && force) {
      onLifetimeParameterValueChange(lifetimeConfigParams.surfacePressure, force / (innerDiameter * bearingWidth));
    }
  }, [showSurfacePressure, innerDiameter, bearingWidth, lifetimeParameters.surfacePressure, lifetimeParameters.force]);

  const [expanded, setExpanded] = useState(isEmbedded || false);

  const hintModal = (
    <Modal
      variant={ModalVariant.ALERT}
      visible={hintMessage !== undefined}
      onClose={() => setHintMessage(undefined)}
      headline={t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })}
    >
      <ul data-testid="life-time-modal-content">{hintMessage}</ul>
    </Modal>
  );

  return (
    <div className={isEmbedded ? 'mt-6' : ''} data-testid="life-time-parameters-form">
      {hintModal}
      {!isEmbedded && (
        <div className="w-full mb-6">
          <button
            className="text-igus-primary"
            type="button"
            onClick={() => {
              const eventAction = expanded
                ? TrackedIbhEventActions.applicationParametersCollapse
                : TrackedIbhEventActions.applicationParametersExpand;
              CustomEvent.trackEvent(IbhEventCategory, eventAction);
              setExpanded(!expanded);
            }}
          >
            <span className="mr-2">{t(`LIFETIME_PARAMETERS_LINK`, { ns: 'bearing-hub/bearingHub' })}</span>
            <div className="-rotate-90 inline-block">
              <Icon size={10} color={IconColor.ORANGE} id={expanded ? IconID.CHEVRON_RIGHT : IconID.CHEVRON_LEFT} />
            </div>
          </button>
        </div>
      )}
      <div
        className={`lg:mx-6 overflow-hidden transition-max-height duration-250 ease-in-out ${
          expanded ? 'max-h-[100%]' : 'max-h-[0px]'
        }`}
      >
        <form id="form">
          {inputsDefinitions.map(inputsDefinition => {
            const currentItemType = getLifetimeParameterType(inputsDefinition.type);

            let buttonGroupElem = null;
            if (currentItemType !== 'default') {
              if (inputsDefinition.type === LifetimeParameterInputSectionType.operation) {
                const operationOptions = [
                  {
                    text: t('LIFETIME.CONTINOUS', { ns: 'bearing-hub/bearingHub' }),
                    value: lifetimeConfigTypeOfOperation.continous,
                  },
                  {
                    text: t('LIFETIME.INTERMITTENT', { ns: 'bearing-hub/bearingHub' }),
                    value: lifetimeConfigTypeOfOperation.intermittent,
                    isDisabled: true,
                  },
                ];
                buttonGroupElem = (
                  <div className="mb-6" data-testid="lifetime_parameter_typeOfOperation">
                    <ButtonGroup<LifetimeConfigTypeOfOperation>
                      buttonGroupDefinition={operationOptions}
                      onButtonClick={(value: LifetimeConfigTypeOfOperation) => {
                        onLifetimeParameterValueChange(lifetimeConfigParams.typeOfOperation, value);
                      }}
                      selectedButton={operationOptions.findIndex(
                        item => item.value === lifetimeParameters.typeOfOperation
                      )}
                    />
                  </div>
                );
              } else if (inputsDefinition.type === LifetimeParameterInputSectionType.motion) {
                const motionOptions = [
                  {
                    text: t('LIFETIME.ROTATION', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.rotation,
                  },
                  {
                    text: t('LIFETIME.LINEAR', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linear,
                  },
                  {
                    text: t('LIFETIME.PIVOTING', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.pivoting,
                  },
                  {
                    text: t('LIFETIME.LINEAR_AND_ROTATION', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linearAndRotation,
                  },
                  {
                    text: t('LIFETIME.LINEAR_AND_PIVOTING', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linearAndPivoting,
                  },
                ];

                buttonGroupElem = (
                  <div className="mb-6" data-testid="lifetime_parameter_typeOfMovement">
                    <ButtonGroup<SpatialTypeOfMovement>
                      buttonGroupDefinition={motionOptions}
                      onButtonClick={(value: SpatialTypeOfMovement) => {
                        onLifetimeParameterValueChange(lifetimeConfigParams.typeOfMovement, value);
                      }}
                      selectedButton={motionOptions.findIndex(item => item.value === lifetimeParameters.typeOfMovement)}
                    />
                  </div>
                );
              }
            }

            return (
              <div className="flex flex-col" key={inputsDefinition.title}>
                <div className="font-bold mb-6">{t(inputsDefinition.title, { ns: 'bearing-hub/bearingHub' })}</div>
                {buttonGroupElem}
                <div className="flex flex-row items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse mb-12">
                  {inputsDefinition.items[currentItemType].map(item => {
                    let inputElem = (
                      <div
                        data-testid={`lifetime-read-only-${item.attribute}`}
                        className="font-bold min-h-[40px] pt-2 text-[#666666]"
                      >
                        {item.defaultValue as string} {item.unit}
                      </div>
                    );
                    const showInput = item.attribute !== lifetimeConfigParams.surfacePressure || showSurfacePressure;
                    if (!item.readOnly && item.inputType === LifetimeParameterInputType.numberInput) {
                      const attributeConfig = defaultLifetimeFormConfig?.[item.attribute] as LifetimeInputFieldConfig;
                      const value = (
                        Math.round((lifetimeParameters[item.attribute] as number) * ROUNDING_PRECISION) /
                          ROUNDING_PRECISION || ''
                      ).toString();
                      const { isValid: validInput, min, max } = inputValidation(value, attributeConfig);
                      inputElem = (
                        <div className={validInput ? '' : '[&_input]:border-red-500'}>
                          <BasicNumberInput
                            id={`filter_${item.attribute}`}
                            unit={item.unit}
                            value={value}
                            unitWidth={20}
                            onBlur={() => {}}
                            onChange={userValue => {
                              onLifetimeParameterValueChange(item.attribute, userValue || null);
                            }}
                          />
                          {!validInput && (
                            <p className="sm:absolute text-xs text-red-500">
                              {t('LIFETIME.PARAMETER_BETWEEN_MIN_MAX', {
                                ns: 'bearing-hub/bearingHub',
                                minValue: `${min} ${item.unit || ''}`,
                                maxValue: `${max} ${item.unit || ''}`,
                              })}
                            </p>
                          )}
                        </div>
                      );
                    } else if (
                      !item.readOnly &&
                      item.inputType === LifetimeParameterInputType.filterSelectAutocomplete
                    ) {
                      inputElem = (
                        <FilterSelectAutocomplete
                          isClearable={false}
                          id={`select_filter_${item.attribute}`}
                          placeholder={t(item.attributeText, { ns: 'bearing-hub/bearingHub' })}
                          hideOptionsModel
                          options={Object.keys(OppositeMaterialMap).map(key => ({
                            label: t(`SHAFT_MATERIAL.${OppositeMaterialMap[key as OppositeMaterialKeyType]}`, {
                              ns: 'bearing-hub/bearingHub',
                            }),
                            value: key,
                          }))}
                          value={{
                            label: t(
                              `SHAFT_MATERIAL.${
                                OppositeMaterialMap[
                                  (lifetimeParameters[item.attribute] || item.defaultValue) as OppositeMaterialKeyType
                                ]
                              }`,
                              {
                                ns: 'bearing-hub/bearingHub',
                              }
                            ),
                            value: (lifetimeParameters[item.attribute] || item.defaultValue) as string,
                          }}
                          onChange={value => {
                            onLifetimeParameterValueChange(item.attribute, value as string);
                          }}
                        />
                      );
                    } else if (!item.readOnly && item.inputType === LifetimeParameterInputType.buttonGroup) {
                      inputElem = (
                        <ButtonGroup<TemporalTypeOfMovement>
                          buttonGroupDefinition={item.buttonGroupDefinition}
                          onButtonClick={(value: TemporalTypeOfMovement) =>
                            onLifetimeParameterValueChange(lifetimeConfigParams.temporalTypeOfMovement, value)
                          }
                          selectedButton={item.buttonGroupDefinition.findIndex(
                            el => el.value === lifetimeParameters.temporalTypeOfMovement
                          )}
                        />
                      );
                    }
                    return (
                      showInput && (
                        <div
                          key={item.attribute}
                          className={`relative mr-6 ${
                            item.inputType !== LifetimeParameterInputType.buttonGroup ? 'w-[150px]' : 'w-full mb-6'
                          }`}
                          data-testid={`lifetime-parameter-input-${item.attribute}`}
                        >
                          <label
                            id={`filter_label_${item.attribute}`}
                            htmlFor={`filter_${item.attribute}`}
                            className="block mb-2 text-sm	"
                          >
                            {t(item.title, { ns: 'bearing-hub/bearingHub' })}
                            {item.hint && (
                              <span
                                className="ml-2 cursor-pointer"
                                data-testid="show-life-time-modal-hint"
                                onClick={event => {
                                  event.stopPropagation();
                                  setHintMessage(item.hint);
                                }}
                              >
                                <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
                              </span>
                            )}
                          </label>
                          {inputElem}
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
            );
          })}
        </form>
        <div className="flex w-full flex-col lg:flex-row justify-end mb-6 items-start lg:items-center">
          {hasLifetimeParameterValues() && (
            <div className="lg:w-[240px]">
              <FilterResetLink
                text={t('LIFETIME.RESTORE_DEFAULT_VALUES', { ns: 'bearing-hub/bearingHub' })}
                dataTestId="remove_all_parameters"
                reset={onReset}
              />
            </div>
          )}
          <div className="lg:w-[240px] w-full">
            <Button
              className={isFormValid ? 'bg-igus-primary w-full' : 'cursor-not-allowed w-full'}
              disabled={!isFormValid}
              icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
              text={t('LIFETIME.CALCUALTE', { ns: 'bearing-hub/bearingHub' })}
              type={TYPE.SECONDARY}
              onClick={() => {
                CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.applicationParametersCalculate);
                setTableHeadersSortingState('lifetime', SortingState.Descending);
                scrollToElement('product-table');
                onCalculate();
              }}
              testId="calculate-lifetime-button"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
